import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Lightning, User, Folder, ListChecks, Truck, ClipboardText, ShieldCheck,
  MagnifyingGlass, Funnel, Clock
} from "@phosphor-icons/react";

const ICON_MAP = {
  folder: Folder, task: ListChecks, vendor: Truck, clipboard: ClipboardText,
  user: User, shield: ShieldCheck, activity: Lightning, clock: Clock, bulk: Lightning,
};

export default function ActivityLogPage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/activity-feed?limit=${LIMIT}`);
      setActivities(data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const getTimeAgo = (ts) => {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const filtered = activities.filter(a => {
    if (filter !== "all" && a.icon !== filter) return false;
    if (search && !a.action?.toLowerCase().includes(search.toLowerCase()) && !a.target?.toLowerCase().includes(search.toLowerCase()) && !a.user_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const paged = filtered.slice(page * LIMIT, (page + 1) * LIMIT);

  return (
    <div data-testid="activity-log-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">Activity Log</h1>
        <p className="text-sm text-muted-foreground mt-1">Full audit trail of platform actions</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search activities..." className="pl-9" data-testid="activity-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px]" data-testid="activity-filter">
            <Funnel size={14} className="mr-1" />
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Activities</SelectItem>
            <SelectItem value="folder">Projects</SelectItem>
            <SelectItem value="task">Tasks</SelectItem>
            <SelectItem value="vendor">Suppliers</SelectItem>
            <SelectItem value="clipboard">Work Orders</SelectItem>
            <SelectItem value="shield">Compliance</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : paged.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Lightning size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-muted-foreground text-sm">No activity found</p>
        </div>
      ) : (
        <div className="border border-border rounded-md divide-y divide-border" data-testid="activity-list">
          {paged.map((a, idx) => {
            const Icon = ICON_MAP[a.icon] || Lightning;
            return (
              <div key={a.id || idx} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors" data-testid={`activity-${a.id}`}>
                <div className="mt-0.5 h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Icon size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">
                    <span className="font-semibold">{a.user_name}</span>
                    <span className="text-muted-foreground mx-1">{a.action}</span>
                    {a.target && <span className="font-medium">{a.target}</span>}
                  </p>
                  {a.project_name && <p className="text-xs text-muted-foreground mt-0.5">in {a.project_name}</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs text-muted-foreground">{getTimeAgo(a.created_at)}</p>
                  <Badge variant="outline" className="text-[9px] mt-0.5">{a.user_role?.replace("_", " ")}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length > LIMIT && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-xs text-muted-foreground">Page {page + 1} of {Math.ceil(filtered.length / LIMIT)}</span>
          <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= filtered.length} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
